import { apiClient } from './client';
import type {
  Company,
  DealerUserPublic,
  DealerAuthResult,
  RegisterDealerInput,
  LoginDealerInput,
  DealerSocialAuthInput,
  CompanyUpdateInput,
  CompanyVideo,
  CompanyGalleryImage,
  CreateCompanyVideoInput,
  CreateCompanyGalleryInput,
  InviteTeamMemberInput,
  VerificationDoc,
  VerificationDocumentType,
} from '@/types';

export const dealerApi = {
  register(input: RegisterDealerInput) {
    return apiClient.post<{ companyId: string; userId: string; message: string }>(
      '/dealer/auth/register',
      input,
    );
  },

  login(input: LoginDealerInput) {
    return apiClient.post<DealerAuthResult>('/dealer/auth/login', input);
  },

  socialRegister(input: DealerSocialAuthInput) {
    return apiClient.post<DealerAuthResult>('/dealer/auth/social', input);
  },

  refresh(refreshToken: string) {
    return apiClient.post<{ token: string }>('/dealer/auth/refresh', { refreshToken });
  },

  verifyEmail(token: string) {
    return apiClient.post<{ message: string }>('/dealer/auth/verify-email', { token });
  },

  forgotPassword(email: string) {
    return apiClient.post<{ message: string }>('/dealer/auth/forgot-password', { email });
  },

  resetPassword(token: string, password: string) {
    return apiClient.post<{ message: string }>('/dealer/auth/reset-password', { token, password });
  },

  logout(refreshToken?: string) {
    return apiClient.post<{ message: string }>('/dealer/auth/logout', { refreshToken });
  },

  me() {
    return apiClient.get<{ user: DealerUserPublic; company: Company }>('/dealer/auth/me');
  },
  // --- Company profile ---
  getCompany() {
    return apiClient.get<Company>('/dealer/company');
  },

  updateCompany(patch: CompanyUpdateInput) {
    return apiClient.patch<Company>('/dealer/company', patch);
  },

  uploadCompanyLogo(file: File) {
    const formData = new FormData();
    formData.append('logo', file);
    return apiClient.post<{ logoUrl: string }>('/dealer/company/logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // --- Discover media (videos + gallery URLs) ---
  listCompanyVideos() {
    return apiClient.get<CompanyVideo[]>('/dealer/company/videos');
  },

  createCompanyVideo(input: CreateCompanyVideoInput) {
    return apiClient.post<CompanyVideo>('/dealer/company/videos', input);
  },

  updateCompanyVideo(id: string, patch: Partial<CreateCompanyVideoInput> & { status?: 'active' | 'hidden' }) {
    return apiClient.patch<CompanyVideo>(`/dealer/company/videos/${id}`, patch);
  },

  deleteCompanyVideo(id: string) {
    return apiClient.delete(`/dealer/company/videos/${id}`);
  },

  listCompanyGallery() {
    return apiClient.get<CompanyGalleryImage[]>('/dealer/company/gallery');
  },

  addCompanyGalleryImage(input: CreateCompanyGalleryInput) {
    return apiClient.post<CompanyGalleryImage>('/dealer/company/gallery', input);
  },

  deleteCompanyGalleryImage(id: string) {
    return apiClient.delete(`/dealer/company/gallery/${id}`);
  },

  // --- Team ---
  listTeam() {
    return apiClient.get<DealerUserPublic[]>('/dealer/team');
  },

  inviteTeamMember(input: InviteTeamMemberInput) {
    return apiClient.post<DealerUserPublic>('/dealer/team/invite', input);
  },

  updateTeamMember(id: string, patch: { name?: string; whatsappNumber?: string; phoneNumber?: string }) {
    return apiClient.patch<DealerUserPublic>('/dealer/team/' + id, patch);
  },

  removeTeamMember(id: string) {
    return apiClient.delete<{ message: string }>('/dealer/team/' + id);
  },

  acceptInvite(token: string, password: string) {
    return apiClient.post<{ message: string }>('/dealer/team/accept-invite', { token, password });
  },


  // --- Verification ---
  listVerificationDocs() {
    return apiClient.get<VerificationDoc[]>('/dealer/company/verification');
  },

  uploadVerificationDoc(file: File, documentType: VerificationDocumentType) {
    const formData = new FormData();
    formData.append('document', file);
    formData.append('documentType', documentType);
    return apiClient.post<VerificationDoc>('/dealer/company/verification', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  deleteVerificationDoc(id: string) {
    return apiClient.delete<{ message: string }>(`/api/dealer/company/verification/${id}`);
  },

  // Plan 4: Dealer car wizard API methods
  listCars(params?: Record<string, string | number>) {
    return apiClient.get('/dealer/cars', { params });
  },
  getDraft(carId: string) {
    return apiClient.get(`/dealer/cars/${carId}`);
  },
  createDraft() {
    return apiClient.post('/dealer/cars');
  },
  saveWizardStep(carId: string, step: number, data: Record<string, unknown>) {
    return apiClient.patch(`/dealer/cars/${carId}/wizard-step`, { step, data });
  },
  publishCar(carId: string) {
    return apiClient.post(`/dealer/cars/${carId}/publish`);
  },
  unpublishCar(carId: string) {
    return apiClient.post(`/dealer/cars/${carId}/unpublish`);
  },
  markSold(carId: string) {
    return apiClient.post(`/dealer/cars/${carId}/mark-sold`);
  },
  markReserved(carId: string) {
    return apiClient.post(`/dealer/cars/${carId}/mark-reserved`);
  },
  deleteCar(carId: string) {
    return apiClient.delete(`/dealer/cars/${carId}`);
  },
  uploadCarPhotos(carId: string, files: File[]) {
    const formData = new FormData();
    files.forEach(f => formData.append('photos', f));
    return apiClient.post(`/dealer/cars/${carId}/photos`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteCarPhoto(carId: string, imageId: string) {
    return apiClient.delete(`/dealer/cars/${carId}/photos/${imageId}`);
  },
  reorderCarPhotos(carId: string, ordering: { imageId: string; sortOrder: number }[]) {
    return apiClient.patch(`/dealer/cars/${carId}/photos/reorder`, { ordering });
  },
  translateText(text: string, targetLang: 'ar' | 'ku' | 'en') {
    return apiClient.post<{ success: boolean; data: { translated: string; targetLang: string } }>('/dealer/translate', { text, targetLang });
  },
  // Stats / analytics
  getStatsOverview() {
    return apiClient.get<{
      success: boolean;
      data: {
        totalListings: number;
        activeListings: number;
        totalViews: number;
        totalLeads: number;
        viewsThisWeek: number;
        leadsThisWeek: number;
      };
    }>('/dealer/stats/overview');
  },

  getTopCars() {
    return apiClient.get<{
      success: boolean;
      data: Array<{
        id: string;
        make: string;
        model: string;
        year: number;
        price: number;
        viewCount: number;
        leadCount: number;
        thumbnailUrl: string | null;
      }>;
    }>('/dealer/stats/top-cars');
  },

  // --- Bulk CSV import ---
  downloadImportTemplate() {
    return apiClient.get('/dealer/bulk-import/template', { responseType: 'blob' });
  },
  validateImport(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post<{
      success: boolean;
      data: {
        totalRows: number;
        valid: Array<{ row: number; data: Record<string, unknown> }>;
        errors: Array<{ row: number; field: string; message: string }>;
      };
    }>('/dealer/bulk-import/validate', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  commitImport(rows: Record<string, unknown>[]) {
    return apiClient.post<{
      success: boolean;
      data: { created: number; ids: string[] };
    }>('/dealer/bulk-import/commit', { rows });
  },

};

// Public lead tracking (no auth required)
export function trackLead(carId: string, eventType: string, userId?: string) {
  return apiClient.post<{ success: boolean }>('/leads/track', { carId, eventType, userId });
}
